import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import API from 'api/API';
import { FileUploadResponse, Inspection } from 'api/Responses/OrderResponses';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import useDeliveryOrdersList from 'hooks/useDeliveryOrdersList';
import { DropTableContainer } from '../DeliveryPortalPage.styles';
import ConfirmOverwriteModal from '../molecules/ConfirmOverwriteModal';
import DeliveryPortalMenu from '../molecules/DeliveryPortalMenu';
import DuplicatesFoundModal from '../molecules/DuplicatesFoundModal';
import DuplicatesTableModal from '../molecules/DuplicatesTableModal';
import FileUploadFilterBar from '../molecules/FileUploadFilterBar';
import FileUploadList from '../molecules/FileUploadList';

export enum DeliveryPortalUploadListStatus {
  INITIALIZING = 'initializing',
  VIEWING = 'viewing',
  UPLOADING_DUPLICATES_FOUND = 'uploadingDuplicatesFound',
  UPLOADING_PROCESS_DUPLICATES = 'uploadingProcessDuplicates',
  UPLOADING_CONFIRM_DUPLICATES = 'uploadingConfirmDuplicates',
  CONFIRMING = 'confirming',
}

const DeliveryPortalUploadList: React.FC = () => {
  const [duplicates, setDuplicates] = useState<Inspection[]>([]);
  const [fileUploadResponse, setFileUploadResponse] = useState<FileUploadResponse | null>(null);
  const [pageState, setPageState] = useState<DeliveryPortalUploadListStatus>(DeliveryPortalUploadListStatus.VIEWING);
  const [language] = useLanguage();
  const { loading, uploads, filter, setFilter, sort, setSort, search, setSearch, setRefresh } = useDeliveryOrdersList();

  /**
   * Calls the API confirming that the uploaded data should go into the DB
   * If there were no duplicates then duplicatesToOverwrite will be empty
   */
  const handleConfirmUpload = useCallback(
    async (uploadId: string | undefined, duplicates: Inspection[]): Promise<void> => {
      const duplicatesToOverwrite: Pick<Inspection, 'gs_one_barcode' | 'ignore'>[] = duplicates
        .filter(d => !d.ignore)
        .map(d => ({ gs_one_barcode: d.gs_one_barcode, ignore: d.ignore }));

      if (!uploadId) {
        setPageState(DeliveryPortalUploadListStatus.VIEWING);
        return;
      }

      try {
        // Publish/Merge file
        await API.orders.mergeUploadOrderDataV2(uploadId, duplicatesToOverwrite);

        setPageState(DeliveryPortalUploadListStatus.VIEWING);
        toast.success(language.DeliveryPortal.Toast.merging);
        setRefresh(true);
      } catch (e) {
        API.errorHandler(e);
        setPageState(DeliveryPortalUploadListStatus.VIEWING);
      }
    },
    [language.DeliveryPortal.Toast.merging, setRefresh],
  );

  /**
   * The initial results from the file upload. Triggers different workflows
   * depending on whether there are duplicates in the response.
   * @param response
   */
  const handleDuplicates = async (response: FileUploadResponse): Promise<void> => {
    setFileUploadResponse(response);
    if (response.duplicates.length > 0) {
      setDuplicates(response.duplicates);
      setPageState(DeliveryPortalUploadListStatus.UPLOADING_DUPLICATES_FOUND);
    } else {
      setDuplicates([]);
      setPageState(DeliveryPortalUploadListStatus.CONFIRMING);
    }
  };

  /**
   * Updates the API indicating that the data should NOT go into the DB
   */
  const handleCancelProcessingDuplicates = async () => {
    if (fileUploadResponse) {
      try {
        await API.orders.cancelUploadOrderDataV2(fileUploadResponse.upload_id);

        setPageState(DeliveryPortalUploadListStatus.UPLOADING_PROCESS_DUPLICATES);
      } catch (e) {
        API.errorHandler(e);
        setPageState(DeliveryPortalUploadListStatus.VIEWING);
      }
    }
  };

  useEffect(() => {
    if (!!fileUploadResponse?.upload_id && pageState === DeliveryPortalUploadListStatus.CONFIRMING) {
      handleConfirmUpload(fileUploadResponse.upload_id, duplicates);
    }
  }, [duplicates, fileUploadResponse?.upload_id, handleConfirmUpload, pageState, uploads]);

  return (
    <Page>
      <Box sx={{ flexDirection: 'column', height: '100%', display: 'flex', overflow: 'hidden' }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" component="h1">
              {language.DeliveryPortal.Title}
            </Typography>
          </Grid>
          <Grid item>
            <DeliveryPortalMenu />
          </Grid>
        </Grid>
        <Box mb={4} />
        {!uploads || loading ? (
          <PageSpinner />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <FileUploadFilterBar
              filter={filter}
              setFilter={setFilter}
              sort={sort}
              setSort={setSort}
              search={search}
              setSearch={setSearch}
              setRefresh={setRefresh}
            />
            <DropTableContainer>
              <FileUploadList data={uploads} handleDuplicates={handleDuplicates} />
            </DropTableContainer>
          </Box>
        )}
      </Box>
      <DuplicatesFoundModal
        isShown={pageState === DeliveryPortalUploadListStatus.UPLOADING_DUPLICATES_FOUND}
        onCancel={() => {
          setPageState(DeliveryPortalUploadListStatus.VIEWING);
        }}
        onConfirm={() => {
          setPageState(DeliveryPortalUploadListStatus.UPLOADING_PROCESS_DUPLICATES);
        }}
      />
      <DuplicatesTableModal
        isShown={pageState === DeliveryPortalUploadListStatus.UPLOADING_PROCESS_DUPLICATES}
        onCancel={() => {
          setPageState(DeliveryPortalUploadListStatus.VIEWING);
        }}
        onConfirm={() => {
          setPageState(DeliveryPortalUploadListStatus.UPLOADING_CONFIRM_DUPLICATES);
        }}
        duplicates={duplicates}
        setDuplicates={setDuplicates}
        setPageState={setPageState}
      />
      <ConfirmOverwriteModal
        isShown={pageState === DeliveryPortalUploadListStatus.UPLOADING_CONFIRM_DUPLICATES && duplicates.length > 0}
        onCancel={handleCancelProcessingDuplicates}
        onConfirm={() => handleConfirmUpload(fileUploadResponse?.upload_id, duplicates)}
        numDuplicates={duplicates.filter(d => d.order_already_exists).length}
        numSetsNotFound={duplicates.filter(d => !d.set_found).length}
        numInvalidGSOneCodes={duplicates.filter(d => !d.valid_gs_one_barcode).length}
      />
    </Page>
  );
};

export default DeliveryPortalUploadList;
